import { inject, Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { toPairs } from "lodash";
import { lastValueFrom } from "rxjs";
import { UserBackend } from "src/app/backend/api/user.backend";
import { Session } from "src/app/backend/models/session";
import { getCurrentAccountNickName } from "src/app/core/account-nickname";
import { getCurrentDomainName } from "src/app/core/domain-name";
import { BootstrapService } from "src/app/modules/bootstrap/bootstrap.service";
import { SessionRepository } from "src/app/repositories/session.repository";
import { LocalStorageService } from "src/app/services/local-storage.service";
import { TitleService } from "src/app/services/title.service";
import { ToastService } from "src/app/services/toast.service";
import { getCustomerNominative } from "src/app/support/customer";
import { dateUtil } from "src/app/support/date-util";
import { getTenantLogoUrl } from "src/app/support/tenant";
import { sntTranslate } from "src/app/support/translate";
import { BeforeUnloadService } from "src/app/services/before-unload.service";

@Injectable()
export class LockService {
  private readonly bootstrapService = inject(BootstrapService);
  private readonly titleService = inject(TitleService);
  private readonly userBackend = inject(UserBackend);
  private readonly toastService = inject(ToastService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly beforeUnloadService = inject(BeforeUnloadService);

  private readonly sessionRepository = new SessionRepository();

  private lockInProgress: boolean = false;

  isLocked(): boolean {
    return !!this.localStorageService.getItem("locked");
  }

  async lock(): Promise<void> {
    if (this.lockInProgress) {
      return;
    }

    this.lockInProgress = true;

    const currentUser = await lastValueFrom(this.userBackend.loadMe());

    if (!currentUser.dynamicLockEnabled) {
      this.toastService.warning(
        sntTranslate(marker("Blocco automatico e pin non attivati"))
      );
      this.lockInProgress = false;
      return;
    }

    try {
      const { otp, salt } = await lastValueFrom(this.userBackend.generateOtp());

      const member = this.bootstrapService.getCurrentMember()!;
      const product = this.bootstrapService.getCurrentProduct();
      const tenant = this.bootstrapService.getCurrentTenant()!;

      const crypetdStorage = await this.localStorageService.crypt(otp, salt);

      const session: Session = {
        memberId: member.id,
        userId: member.user?.id,
        userNominative: getCustomerNominative(member),
        createdAt: dateUtil().toISOString(),
        product: product,
        data: crypetdStorage,
        tenantName: tenant.name,
        accountNickName: getCurrentAccountNickName(),
        domainName: getCurrentDomainName(),
        userAvatar: "assets/images/staff_placeholder.png",
        tenantLogoUrl: getTenantLogoUrl(tenant.logo!),
        url: `${location.pathname}${location.search}`,
        pageTitle: this.titleService.getTitle(),
        pageIcon: this.titleService.getIcon(),
        salt: salt
      };

      await this.sessionRepository.deleteByMemberAccountDomain({
        memberId: member.id,
        accountNickName: getCurrentAccountNickName(),
        domainName: getCurrentDomainName()
      });

      await this.sessionRepository.create(session);

      this.localStorageService.clear();
      this.localStorageService.setItem("locked", "true");
      this.localStorageService.setItem("locked-domain", getCurrentDomainName());
      this.localStorageService.setItem(
        "locked-account",
        getCurrentAccountNickName()
      );

      await this.beforeUnloadService.forceRelease();
      await this.lockReload();
    } catch (ex) {
      this.toastService.error(
        sntTranslate(
          marker(
            "Blocco della sessione fallito, assicurati di aver configurato il pin"
          )
        )
      );
    } finally {
      this.lockInProgress = false;
    }
  }

  private async lockReload(): Promise<void> {
    window.location.href = "/";
  }

  async unlock({
    pin,
    salt,
    data,
    sessionId,
    unlockLocation
  }: {
    salt: string;
    pin: string;
    data: ArrayBuffer;
    sessionId: number;
    unlockLocation: string;
  }): Promise<boolean> {
    const decriptedStorage = await this.localStorageService.uncrypt(
      pin,
      salt,
      data
    );

    const decriptedStorageObject = JSON.parse(decriptedStorage);

    this.localStorageService.clear();

    toPairs(decriptedStorageObject).forEach(([key, value]) => {
      if (!["locked", "locked-domain", "locked-account"].includes(key)) {
        this.localStorageService.setItem(key, <any>value);
      }
    });

    await this.sessionRepository.delete(sessionId);

    window.location.href = unlockLocation;

    // TODO probabilmente il return boolean non serve più
    return true;
  }
}
